import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import TableActionButton from "@components/buttons/TableActionButton";
import CopyButton from "@components/buttons/CopyButton";
import { affiliateApis } from "@services/apis/Affiliate";
import {
  AccountApprovalsData,
  AccountApprovalsResponse,
} from "@services/models/Affiliate/AccountApprovalsModel";
import { UpdateApprovalStatusRequest } from "@services/models/Affiliate/UpdateApprovalStatusModel";
import { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ConstantFunctions from "../../utils/constants_functions";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import AffiliateStatusComponent from "./components/AffiliateStatusPill";

const AccountApprovals = () => {
  const constantFunctions = new ConstantFunctions();
  const gridRef = useRef<any>(null);

  const [approvals, setApprovals] = useState<AccountApprovalsData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const rowsPerPage = 1000;

  const { handleError, handleSuccess } = useSnackbarMessage();
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  // AG-Grid Column Definitions
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Date",
        field: "createdAt",
        flex: 1.2,
        minWidth: 140,
        maxWidth: 200,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
        cellRenderer: (params: any) => {
          return constantFunctions.dateFormatInDDMMYY(params.data.createdAt);
        },
      },
      {
        headerName: "Affiliate User ID",
        field: "userId",
        flex: 1.5,
        minWidth: 180,
        maxWidth: 280,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center justify-between">
              <span className="flex-1">{params.data.userId}</span>
              <CopyButton
                copyText={params.data.userId.toString()}
                copyMessage="Affiliate User ID copied!"
                buttonSize="14px"
                copyIconSize={10}
                borderRadius="2px"
              />
            </div>
          );
        },
      },
      {
        headerName: "Affiliate Email",
        field: "emailId",
        flex: 2.5,
        minWidth: 300,
        maxWidth: 450,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center justify-between">
              <span className="flex-1 truncate pr-1">
                {params.data.emailId}
              </span>
              <CopyButton
                copyText={params.data.emailId}
                copyMessage="Affiliate Email copied!"
                buttonSize="14px"
                copyIconSize={10}
                borderRadius="2px"
              />
            </div>
          );
        },
      },
      {
        headerName: "Status",
        field: "status",
        flex: 1.3,
        minWidth: 150,
        maxWidth: 220,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <AffiliateStatusComponent
              statusText={params.data.status.toUpperCase()}
            />
          );
        },
      },
      {
        headerName: "Accept Application",
        field: "actions",
        flex: 1.8,
        minWidth: 200,
        maxWidth: 300,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          if (params.data.status === "requested") {
            return (
              <div className="flex flex-row items-center gap-1.5 justify-end">
                <TableActionButton
                  variant="danger"
                  icon="cancel"
                  loading={isButtonLoading}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleApprovalStatus(params.data.id, "rejected");
                  }}
                >
                  Reject
                </TableActionButton>
                <TableActionButton
                  variant="success"
                  icon="check_circle"
                  loading={isButtonLoading}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleApprovalStatus(params.data.id, "approved");
                  }}
                >
                  Approve
                </TableActionButton>
              </div>
            );
          }
          return null;
        },
      },
    ],
    [isButtonLoading],
  );

  const onBodyScroll = (params: any) => {
    const scrollTop = params.top;
    const scrollHeight = params.api.getDisplayedRowCount() * 48;
    const clientHeight =
      params.api.getVerticalPixelRange().bottom -
      params.api.getVerticalPixelRange().top;

    if (scrollTop + clientHeight >= scrollHeight - 100 && hasMore && !loading) {
      loadMoreApprovals();
    }
  };

  const fetchApprovalList = useCallback(
    async (pageNumber: number, append: boolean) => {
      try {
        setLoading(true);
        const response: AccountApprovalsResponse =
          await affiliateApis.getAccountApprovals({
            page: pageNumber,
            limit: rowsPerPage,
          });

        const fetched = response.data;
        setApprovals((prev) => (append ? [...prev, ...fetched] : fetched));
        setHasMore(fetched.length === rowsPerPage);
      } catch (err: any) {
        setError("Failed to fetch approval data");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const loadMoreApprovals = useCallback(() => {
    if (loading || !hasMore) return;
    setPage((prev) => {
      const next = prev + 1;
      fetchApprovalList(next, true);
      return next;
    });
  }, [loading, hasMore, fetchApprovalList]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchApprovalList(1, false);
  }, [fetchApprovalList]);

  const handleApprovalStatus = async (
    id: number,
    status: "approved" | "rejected",
  ) => {
    try {
      setIsButtonLoading(true);
      const updateRequest: UpdateApprovalStatusRequest = {
        id: id,
        status: status,
      };

      const data =
        await affiliateApis.updateAccountApprovalRequest(updateRequest);

      setApprovals((prevApprovals) =>
        prevApprovals.map((approval) => {
          if (approval.id === id) {
            return {
              ...approval,
              status: status,
            };
          }
          return approval;
        }),
      );

      handleSuccess({ message: data.msg || "Status updated successfully" });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Updating status failed. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setIsButtonLoading(false);
    }
  };

  return (
    <div
      className="flex flex-row w-[calc(100vw-150px)]"
      style={{ height: "calc(100dvh - 120px)" }}
    >
      <div className="flex flex-col bg-bg-page-body py-[20px] w-full h-full overflow-hidden">
        {loading && approvals.length === 0 ? (
          <AgGridSkeleton
            columnDefs={columnDefs}
            style={{ height: "100%", width: "100%" }}
          />
        ) : (
          <AgGridReactTable
            ref={gridRef}
            rowData={approvals}
            columnDefs={columnDefs}
            onBodyScroll={onBodyScroll}
            getRowId={(params) => params.data.id.toString()}
            error={error}
          />
        )}
      </div>
    </div>
  );
};

export default AccountApprovals;
